import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Heart, Search, SlidersHorizontal } from "lucide-react";
import ShareButton from "../components/ShareButton";
import api from "../lib/api";
import DesignTile from "../components/DesignTile";
import { useSEO } from "../lib/seo";
import { useWishlist } from "../lib/wishlist";

const PAGE_SIZE = 24;

export default function Designs() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState(["All"]);
  const [colors, setColors] = useState(["All"]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { isWishlisted, toggleWishlist } = useWishlist();

  const q = params.get("q") || "";
  const category = params.get("category") || "All";
  const color = params.get("color") || "All";
  const sort = "id";

  useSEO({
    title: "Designs — PrintMyTees",
    description: "Browse PrintMyTees designs and filter by design category, apparel type, search and sort.",
  });

  useEffect(() => {
    api.get("/design-categories")
      .then((r) => {
        const values = Array.isArray(r.data?.categories) ? r.data.categories : [];
        setCategories(["All", ...values.filter(Boolean)]);
      })
      .catch(() => setCategories(["All"]));

    api.get("/design-colors")
      .then((r) => {
        const values = Array.isArray(r.data?.colors) ? r.data.colors : [];
        setColors(["All", ...values.filter(Boolean)]);
      })
      .catch(() => setColors(["All"]));
  }, []);

  const fetchPage = async (skip, replace) => {
    if (skip === 0) setLoading(true);
    else setMore(true);

    try {
      const requestParams = { q: q.trim(), skip, limit: PAGE_SIZE, category, color, sort, ...(category === "All" ? { exclude_category: "Tamil" } : {}) };
      const r = await api.get("/designs", { params: requestParams });

      setTotal(r.data?.total || 0);
      const newItems = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems((prev) => replace ? newItems : [...prev, ...newItems]);
    } catch {
      if (replace) {
        setItems([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setMore(false);
    }
  };

  useEffect(() => {
    fetchPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, color, sort]);

  const setParam = (key, value, defaultValue = "All") => {
    const next = new URLSearchParams(params);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const setQ = (value) => setParam("q", value, "");

  const reset = () => {
    setParams({});
    setFiltersOpen(false);
  };

  const activeFilters = [
    category !== "All" ? ["Category", category, () => setParam("category", "All")] : null,
    color !== "All" ? ["Color", color, () => setParam("color", "All")] : null,
    q ? ["Search", q, () => setParam("q", "", "")] : null,
  ].filter(Boolean);

  const canLoadMore = items.length < total;
  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="designs-page">
      <section className="relative isolate overflow-hidden border-b border-[#E6DFD5] bg-[#171716] text-[#FAF7F2]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse at 82% 18%, rgba(217,83,40,.22), transparent 36%), radial-gradient(ellipse at 8% 100%, rgba(217,83,40,.12), transparent 35%)" }} />
        <div aria-hidden="true" className="pointer-events-none absolute right-0 top-0 h-full w-[38%] opacity-[0.08]" style={{ backgroundImage: "linear-gradient(135deg, transparent 48%, #f7a06e 49%, transparent 50%)", backgroundSize: "30px 30px" }} />
        <div className="container-pmt relative py-6 sm:py-8 lg:py-10">
          <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-[0.24em] text-[#F17A45] sm:text-[10px]"><span className="h-px w-7 bg-[#F17A45]" />PrintMyTees <span className="text-white/35">/</span> Design Studio</div>
          <div className="mt-3 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div><h1 className="max-w-3xl text-[2.15rem] font-black leading-[1.08] tracking-[-0.015em] sm:text-5xl sm:leading-[1.1] sm:tracking-[-0.02em] lg:text-6xl" data-testid="designs-heading">Find your <span className="text-[#F17A45]">next favourite.</span></h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-stone-300 sm:mt-4 sm:text-base sm:leading-7">Explore original prints, Tamil-inspired artwork and everyday streetwear designs. Pick a style, then make it yours.</p></div>
            <div className="flex items-center gap-4 border-t border-white/15 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
              <div><div className="text-2xl font-black text-white sm:text-3xl">{total}</div><div className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-stone-400">Designs</div></div>
              <div className="h-10 w-px bg-white/15" />
              <div><div className="text-2xl font-black text-[#F17A45] sm:text-3xl">01</div><div className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-stone-400">Your style</div></div>
            </div>
          </div>
        </div>
      </section>
      <div className="container-pmt py-3 sm:py-4 lg:py-5">

      <div className="mt-1 rounded-2xl border border-[#E6DFD5] bg-[#FFFEFC] p-3 shadow-[0_12px_32px_rgba(44,31,21,.055)] sm:rounded-[22px] sm:p-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setQ("");
                }
              }}
              placeholder="Search design ID, name or category..."
              className="input-pmt pl-10 !rounded-xl !border-[#E6DFD5] !bg-[#FAF7F2] focus:!border-[#D95328]"
              data-testid="designs-search"
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`h-11 w-12 shrink-0 rounded-xl border border-[#E6DFD5] bg-[#171716] text-white flex items-center justify-center transition hover:bg-[#D95328] ${filtersOpen ? "border-[#D95328] bg-[#D95328]" : ""}`}
            aria-label={filtersOpen ? "Close design filters" : "Open design filters"}
            title={filtersOpen ? "Close filters" : "Filters"}
            data-testid="designs-filter-btn"
          >
            <SlidersHorizontal size={17} />
          </button>
        </div>

        {filtersOpen && (
          <div className="relative mt-3">
            <div className="rounded-xl border border-[#E6DFD5] bg-[#FAF7F2] p-4 shadow-inner">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                    T-Shirt Color
                  </label>
                  <select
                    className="input-pmt"
                    value={color}
                    onChange={(e) => setParam("color", e.target.value)}
                    data-testid="designs-color-filter"
                  >
                    {colors.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                    Design Category
                  </label>
                  <select
                    className="input-pmt"
                    value={category}
                    onChange={(e) => setParam("category", e.target.value)}
                    data-testid="designs-category-filter"
                  >
                    {categories.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </div>
              </div>

              {(category !== "All" || color !== "All") && (
                <button
                  type="button"
                  onClick={reset}
                  className="mt-3 text-xs underline text-stone-500 hover:text-[#D95328]"
                  data-testid="designs-reset"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2 items-center">
          {activeFilters.map(([label, value, remove]) => (
            <button key={label} type="button" onClick={remove} className="chip text-[10px] py-1 px-2">
              {label}: {value} ×
            </button>
          ))}
          <span className="ml-auto text-xs font-semibold uppercase tracking-wider text-stone-500">{total} results</span>
        </div>
      </div>
      {category !== "All" && (
        <div className="mt-8">
          <div className="overline">Design Category</div>
          <h2 className="mt-1 text-3xl font-bold uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {category} Designs
          </h2>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 items-stretch gap-3 sm:mt-7 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
        {loading && Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="aspect-square rounded-2xl bg-[#EDE5DA] animate-pulse" />
        ))}

        {!loading && items.map((design) => {
          const wished = isWishlisted("design", design.design_id);
          return (
            <div
              key={design.design_id}
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/products?design=${encodeURIComponent(design.design_id)}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/products?design=${encodeURIComponent(design.design_id)}`);
                }
              }}
              className="group relative flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-[20px] border border-[#E5DCCE] bg-white shadow-[0_8px_24px_rgba(45,32,22,.055)] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#D6A58E] hover:shadow-[0_22px_48px_rgba(45,32,22,.14)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2"
              data-testid={`design-card-${design.design_id}`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWishlist({
                  type: "design",
                  id: design.design_id,
                  name: design.name,
                  image: design.image_url,
                  price: design.price,
                  category: design.category,
                  group: design.group,
                  });
                }}
                className="absolute z-20 top-2 right-2 h-9 w-9 sm:top-3 sm:right-3 sm:h-9 sm:w-9 rounded-full flex items-center justify-center hover:text-[#D95328]"
                aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                title={wished ? "Remove from wishlist" : "Add to wishlist"}
                data-testid={`design-wishlist-${design.design_id}`}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E6DFD5] bg-white/95 shadow-sm"><Heart size={17} fill={wished ? "currentColor" : "none"} className={wished ? "text-[#D95328]" : ""} /></span>
              </button>
              <ShareButton
                title={design.name}
                url={`/products?design=${encodeURIComponent(design.design_id)}`}
                className="absolute z-30 top-2 left-2"
              />

              <div className="relative overflow-hidden border-b border-[#EAE1D6] bg-[radial-gradient(ellipse_at_50%_35%,#FFFFFF_0%,#F7F2EA_72%,#EEE5D9_100%)] p-2.5 sm:p-3">
                <span className="pointer-events-none absolute bottom-4 left-4 z-10 rounded-full border border-[#E8DFD4] bg-white/90 px-2.5 py-1 text-[8px] font-bold uppercase tracking-[0.16em] text-stone-600 shadow-sm sm:bottom-5 sm:left-5 sm:text-[9px]">Studio selection</span>
                <div className="overflow-hidden rounded-xl border border-white/80 bg-white shadow-[0_4px_18px_rgba(35,25,16,.04)]">
                  <DesignTile design={design} />
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-col p-3.5 sm:p-4">
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-stone-500 sm:text-[10px]">
                  <span className="font-mono tracking-[0.14em]">{design.design_id}</span>
                  <span className="rounded-full bg-[#F5F0E8] px-2 py-1 text-[8px] tracking-[0.12em] text-stone-600 sm:text-[9px]">{design.category || design.group}</span>
                </div>
                <div className="mt-2 min-h-[2.3rem] break-words text-[16px] font-bold uppercase leading-[1.12] tracking-[0.01em] text-[#171716] sm:text-[18px]" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {design.name}
                </div>
                {design.description && <div className="mt-1.5 min-h-[2.25rem] text-[11px] leading-[1.45] text-stone-500 line-clamp-2 sm:text-xs">{design.description}</div>}

                {(() => {
                  const basePrice = Number(design.price || 0);
                  const discountPercent = Math.min(100, Math.max(0, Number(design.discount_percent || 0)));
                  const sellingPrice = basePrice > 0
                    ? Math.round(basePrice * (1 - discountPercent / 100))
                    : 0;

                  if (!sellingPrice) return null;

                  return (
                    <div className="mt-3 flex min-h-8 items-center gap-2 flex-wrap border-t border-[#F0E9E0] pt-2.5">
                      {discountPercent > 0 && (
                        <>
                          <span className="text-sm text-stone-500 line-through">
                            ₹{basePrice.toLocaleString("en-IN")}
                          </span>
                          <span className="rounded-sm bg-[#FCE7E2] px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-[#B83F25]">
                            {discountPercent}% OFF
                          </span>
                        </>
                      )}
                      <span
                        className="text-2xl font-bold tracking-tight text-[#171716]"
                        style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                      >
                        ₹{sellingPrice.toLocaleString("en-IN")}
                      </span>
                    </div>
                  );
                })()}

                <div className="mt-auto pt-4">
                  <Link
                    to={`/products?design=${encodeURIComponent(design.design_id)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="btn-primary block w-full rounded-lg py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.16em] transition-all duration-200 hover:shadow-[0_8px_20px_rgba(217,83,40,.2)]"
                    data-testid={`buy-design-${design.design_id}`}
                  >
                    Buy Now
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && items.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-[#D9C8B8] bg-white py-16 text-center text-stone-500" data-testid="designs-empty">
          No designs found. Try another category or search.
        </div>
      )}

      {canLoadMore && (
        <div className="mt-10 flex justify-center" data-testid="designs-load-more-wrap">
          <button
            type="button"
            onClick={() => fetchPage(items.length, false)}
            disabled={more}
            className="btn-outline-pmt disabled:opacity-50"
            data-testid="designs-load-more"
          >
            {more ? "Loading..." : `Load more (${total - items.length} left)`}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
